import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, CreditCard, RefreshCw } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';

// Card billing for a client's block hours: the monthly price, the card on
// file, and whether Stripe is billing it. Billing roles only.
//
// Two operator buttons, so nothing here ever needs a person to touch
// Stripe or the database by hand:
//   - "Set up card billing" makes a link for a client who signed before
//     card billing existed; they save a card and billing starts the 1st.
//   - "Retry" pushes our state to Stripe again after a failure (start the
//     subscription, or set / clear its stop date after notice).

interface Billing {
  subscriptionId: string;
  monthlyHours: number;
  monthlyPriceCents: number | null;
  overageRateCents: number;
  card: { brand: string | null; last4: string | null } | null;
  stripeStatus: string;
  stripeBillingFrom: string | null;
  stripeLastError: string | null;
  setupLink: { url: string; expiresAt: string } | null;
}

const money = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const longDate = (iso: string): string =>
  new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

function statusLine(b: Billing): string {
  switch (b.stripeStatus) {
    case 'none':
      return 'Not billed by card - no card on file. Billed outside the app.';
    case 'pending':
      return 'Card on file; Stripe billing has not started yet.';
    case 'trialing':
      return `Billing by card from ${b.stripeBillingFrom ? longDate(b.stripeBillingFrom) : 'the next 1st'}, on the 1st of each month.`;
    case 'active':
      return 'Billing by card on the 1st of each month.';
    case 'past_due':
      return 'The last monthly charge failed. Stripe will retry; the client can update their card from the invoice.';
    // The live account marks a subscription unpaid once every retry has
    // failed (2026-10-16). Stripe has stopped trying at that point, so
    // "will retry" would be wrong.
    case 'unpaid':
      return 'Every retry of the monthly charge failed and Stripe has stopped trying. Call the client: they need to pay the open invoice or update their card.';
    case 'canceled':
      return 'Card billing has ended.';
    default:
      return `Stripe status: ${b.stripeStatus}.`;
  }
}

export function BlockHoursBillingCard({ organizationId }: { organizationId: string }): React.ReactElement | null {
  const [billing, setBilling] = useState<Billing | null>(null);
  const [stripeConfigured, setStripeConfigured] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ billing: Billing | null; stripeConfigured: boolean }>(
        `/api/block-hours/${organizationId}/billing`,
      );
      setBilling(data.billing);
      setStripeConfigured(data.stripeConfigured);
      if (data.billing?.monthlyPriceCents) setPrice(String(data.billing.monthlyPriceCents / 100));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load billing.');
    } finally {
      setLoaded(true);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function post(path: string, json?: unknown): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch<{ billing: Billing; stripeError?: string | null }>(
        `/api/block-hours/${organizationId}/billing/${path}`,
        { method: 'POST', json: json ?? {} },
      );
      setBilling(data.billing);
      if (data.stripeError) setError(data.stripeError);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded || !billing) return null;

  const priceCents = Math.round(Number(price) * 100);
  return (
    <Card>
      <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-header)]">
        <CreditCard className="h-4 w-4 text-[var(--color-muted)]" aria-hidden="true" />
        Card billing
      </h2>
      <p className="mt-1 text-sm text-[var(--color-body)]">
        {billing.monthlyHours} hours a month
        {billing.monthlyPriceCents ? ` at ${money(billing.monthlyPriceCents)}` : ''} · overage{' '}
        {money(billing.overageRateCents)}/hr
      </p>
      <p className="mt-2 text-sm text-[var(--color-muted)]">{statusLine(billing)}</p>
      {billing.card?.last4 && (
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Card: {billing.card.brand ? billing.card.brand[0]!.toUpperCase() + billing.card.brand.slice(1) : 'Card'} ending{' '}
          {billing.card.last4}
        </p>
      )}

      {(error ?? billing.stripeLastError) && (
        <div className="mt-3 rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {error ?? billing.stripeLastError}
        </div>
      )}

      {billing.stripeLastError && (
        <div className="mt-3">
          <Button variant="outline" onClick={() => void post('sync')} disabled={busy}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> {busy ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      )}

      {billing.stripeStatus === 'none' &&
        (billing.setupLink ? (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-[var(--color-body)]">
              Send this link to the client. It expires on {longDate(billing.setupLink.expiresAt)}.
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={billing.setupLink.url}
                onFocus={(e) => e.currentTarget.select()}
                className="block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-xs"
                aria-label="Card billing setup link"
              />
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(billing.setupLink!.url);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="inline-flex shrink-0 items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-t3-blue)] px-3 py-2 text-xs font-semibold text-[var(--color-t3-blue)]"
              >
                {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        ) : stripeConfigured ? (
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-muted)]">Monthly price ($)</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="block w-36 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
                aria-label="Monthly price"
              />
            </label>
            <Button
              onClick={() => void post('setup-link', { monthlyPriceCents: priceCents })}
              disabled={busy || !(priceCents >= 100)}
            >
              Set up card billing
            </Button>
          </div>
        ) : (
          <p className="mt-3 text-sm text-[var(--color-muted)]">
            Card billing becomes available once Stripe keys are configured.
          </p>
        ))}
    </Card>
  );
}
